import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Sparkles, Zap, Lock, Gift, Infinity as InfinityIcon, Clock } from "lucide-react";
import { useAiCreditWallet, formatRefillCountdown } from "@/hooks/useAiCredits";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  workspaceId: string;
}

export function AiCreditWalletDialog({ open, onOpenChange, workspaceId }: Props) {
  const { data: wallet } = useAiCreditWallet(workspaceId);
  const free = wallet?.free ?? 0;
  const paid = wallet?.paid ?? 0;
  const quota = wallet?.freeDailyQuota ?? 5;
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const refillIn = wallet ? formatRefillCountdown(wallet.nextRefillAt) : "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-sm">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="flex flex-col">
              <span className="text-base font-semibold">AI Credits Wallet</span>
              <span className="text-xs font-normal text-muted-foreground">Power AI Fetch & analysis on your leads</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Total balance hero */}
          <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5">
            <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-primary/10 blur-2xl" />
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Current balance</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-4xl font-bold tracking-tight text-foreground">{free + paid}</span>
                  <span className="text-sm text-muted-foreground">credits</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">1 credit = 1 AI Fetch + Analysis</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15">
                <Zap className="h-6 w-6 text-primary" />
              </div>
            </div>
          </div>

          {/* Free vs Paid breakdown */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border bg-card p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600">
                  <Gift className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm font-semibold">Daily free</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold tabular-nums">{free}</span>
                <span className="text-xs text-muted-foreground">/ {quota} today</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>Resets in {refillIn} · No rollover</span>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <InfinityIcon className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm font-semibold">Paid</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold tabular-nums">{paid}</span>
                <span className="text-xs text-muted-foreground">credits</span>
              </div>
              <div className="mt-1.5 text-[11px] text-muted-foreground">
                Never expire · Roll over forever
              </div>
            </div>
          </div>

          <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
            We use your <span className="font-medium text-foreground">free credits first</span>, then your paid credits.
            Free credits refresh every day at midnight UTC and don't carry over. Paid credits never expire.
          </div>

          <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Need more credits? Contact your account manager.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
